/**
 * La scheda "Fatture" del controllo di gestione.
 *
 * Tre momenti, nell'ordine in cui capitano: si caricano gli XML, si dice una
 * volta sola a quale ingrediente corrisponde ogni voce, e da li' in poi si
 * guarda quanto stiamo pagando davvero rispetto a quello che c'e' scritto in
 * anagrafica.
 *
 * Le fatture non si sommano ai movimenti bancari e non entrano nel conto
 * economico: quella e' la stessa spesa vista da un'altra parte, e sommarle
 * significherebbe contarla due volte.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Upload, FileText, Link2, TrendingUp, AlertTriangle } from 'lucide-react';
import ApiService, {
  CeFattura, CeEsitoImportFatture, RigaFatturaDaAbbinare, PrezzoDalleFatture,
  CeFornitore
} from '../services/api';
import { Ingrediente } from '../types';
import {
  Button, Card, CardBody, Alert, Badge, Select, Input, Label
} from './ui';

const Sezione = styled.section`
  margin-bottom: var(--space-2xl, 2rem);
`;

const Titolo = styled.h3`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin: 0 0 var(--space-sm);
  font-size: var(--font-size-md);
`;

const Nota = styled.p`
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  margin: 0 0 var(--space-md);
  max-width: 48rem;
  line-height: 1.5;
`;

const Tabella = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);

  th, td {
    padding: var(--space-sm);
    text-align: left;
    border-bottom: 1px solid var(--border-color);
    vertical-align: top;
  }
  th { color: var(--text-secondary); font-weight: var(--font-weight-medium); }
  td.numero, th.numero { text-align: right; font-variant-numeric: tabular-nums; }
`;

const Barra = styled.div`
  display: flex;
  gap: var(--space-md);
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: var(--space-md);
`;

const Scarto = styled.span<{ $grave: boolean }>`
  color: ${(p) => (p.$grave ? 'var(--danger, #c0392b)' : 'var(--text-secondary)')};
  font-weight: ${(p) => (p.$grave ? 'var(--font-weight-semibold)' : 'inherit')};
`;

const euro = (valore: number | string | null | undefined) =>
  `€ ${Number(valore || 0).toLocaleString('it-IT', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  })}`;

const FattureTab: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [inCorso, setInCorso] = useState(false);
  const [esito, setEsito] = useState<CeEsitoImportFatture | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  const [fatture, setFatture] = useState<CeFattura[]>([]);
  const [daAbbinare, setDaAbbinare] = useState<RigaFatturaDaAbbinare[]>([]);
  const [prezzi, setPrezzi] = useState<PrezzoDalleFatture[]>([]);
  const [ingredienti, setIngredienti] = useState<Ingrediente[]>([]);
  const [fornitori, setFornitori] = useState<CeFornitore[]>([]);
  const [scelte, setScelte] = useState<Record<string, number | 'nessuno' | ''>>({});

  const chiaveDi = (riga: RigaFatturaDaAbbinare) =>
    `${riga.fornitore_id ?? 0}|${riga.codice_articolo ?? riga.descrizione}`;

  const carica = useCallback(async () => {
    const [f, r, p, i, fo] = await Promise.all([
      ApiService.getFatture(),
      ApiService.getRigheFatturaDaAbbinare(),
      ApiService.getPrezziDalleFatture(),
      ApiService.getIngredienti(false),
      ApiService.getFornitori({ soloAttivi: true })
    ]);
    setFatture(f);
    setDaAbbinare(r);
    setPrezzi(p.prezzi);
    setIngredienti(i);
    setFornitori(fo);
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const importa = async () => {
    if (!files.length) return;
    setInCorso(true);
    setErrore(null);
    setEsito(null);
    try {
      setEsito(await ApiService.importaFattureElettroniche(files));
      setFiles([]);
      await carica();
    } catch (e: any) {
      setErrore(e?.response?.data?.error || "Errore nell'import delle fatture");
    } finally {
      setInCorso(false);
    }
  };

  const abbina = async (riga: RigaFatturaDaAbbinare) => {
    const scelta = scelte[chiaveDi(riga)];
    if (!scelta) return;
    await ApiService.abbinaRigaFattura({
      fornitoreId: riga.fornitore_id,
      codiceArticolo: riga.codice_articolo,
      descrizione: riga.descrizione,
      ingredienteId: scelta === 'nessuno' ? null : Number(scelta)
    });
    await carica();
  };

  const attribuisci = async (fattura: CeFattura, fornitoreId: number) => {
    await ApiService.attribuisciFattura(fattura.id, fornitoreId);
    await carica();
  };

  const senzaFornitore = fatture.filter((f) => !f.fornitore_id);

  return (
    <>
      <Sezione>
        <Alert $variant="info">
          Carica gli XML della fattura elettronica, anche firmati (.p7m). Il fornitore
          si riconosce dalla partita IVA, non dal nome: la ragione sociale sul documento
          quasi mai coincide con come lo chiamiamo noi. Le fatture non entrano nel conto
          economico — quello lo fanno i movimenti bancari — servono a sapere <em>cosa</em>
          abbiamo comprato e a che prezzo.
        </Alert>

        <Barra>
          <input
            type="file"
            multiple
            accept=".xml,.p7m,application/xml,text/xml"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
          <Button onClick={importa} disabled={!files.length || inCorso}>
            <Upload size={16} /> {inCorso ? 'Importo…' : `Importa ${files.length || ''}`}
          </Button>
        </Barra>

        {errore && <Alert $variant="error">{errore}</Alert>}

        {esito && (
          <Alert $variant={esito.totali.fileNonLetti ? 'warning' : 'success'}>
            {esito.totali.fattureImportate} fatture importate, {esito.totali.righe} righe
            di cui {esito.totali.righeAbbinate} riconosciute da sole.
            {esito.totali.fileDuplicati > 0
              && ` ${esito.totali.fileDuplicati} file erano gia' stati caricati.`}
            {esito.totali.daAttribuire > 0
              && ` ${esito.totali.daAttribuire} fatture non hanno un fornitore in anagrafica.`}
            {esito.nonLetti.length > 0 && (
              <ul>
                {esito.nonLetti.map((n) => <li key={n.file}>{n.file}: {n.errore}</li>)}
              </ul>
            )}
          </Alert>
        )}
      </Sezione>

      {senzaFornitore.length > 0 && (
        <Sezione>
          <Titolo><AlertTriangle size={18} /> Fatture senza fornitore</Titolo>
          <Nota>
            La partita IVA di queste fatture non corrisponde a nessuno in anagrafica.
            Attribuiscile una volta: la partita IVA viene registrata sul fornitore e da
            li' in poi le sue fatture si riconoscono da sole.
          </Nota>
          <Card><CardBody>
            <Tabella>
              <thead>
                <tr>
                  <th>Sul documento</th>
                  <th>Partita IVA</th>
                  <th>Numero</th>
                  <th className="numero">Totale</th>
                  <th>Fornitore</th>
                </tr>
              </thead>
              <tbody>
                {senzaFornitore.map((fattura) => (
                  <tr key={fattura.id}>
                    <td>{fattura.fornitore_denominazione}</td>
                    <td>{fattura.fornitore_partita_iva}</td>
                    <td>{fattura.numero}</td>
                    <td className="numero">{euro(fattura.totale)}</td>
                    <td>
                      <Select
                        defaultValue=""
                        onChange={(e) => e.target.value
                          && attribuisci(fattura, Number(e.target.value))}
                      >
                        <option value="">Scegli…</option>
                        {fornitori.map((f) => (
                          <option key={f.id} value={f.id}>{f.nome}</option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabella>
          </CardBody></Card>
        </Sezione>
      )}

      <Sezione>
        <Titolo>
          <Link2 size={18} /> Voci da abbinare
          {daAbbinare.length > 0 && <Badge $variant="warning">{daAbbinare.length}</Badge>}
        </Titolo>
        <Nota>
          In fattura c'e' scritto "PASTA PISTACCHIO BRONTE 3,5KG cod. 4471", da noi
          l'ingrediente si chiama "Pistacchio": il legame non si indovina, lo decidi tu la
          prima volta e poi vale da solo per tutte le fatture successive. Le voci che non
          sono ingredienti — trasporto, imballi, cauzioni — marcale come tali: non
          torneranno a chiedere.
        </Nota>

        {daAbbinare.length === 0 ? (
          <Alert $variant="success">Nessuna voce in attesa.</Alert>
        ) : (
          <Card><CardBody>
            <Tabella>
              <thead>
                <tr>
                  <th>Voce in fattura</th>
                  <th>Fornitore</th>
                  <th className="numero">Volte</th>
                  <th className="numero">Importo</th>
                  <th>Corrisponde a</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {daAbbinare.map((riga) => (
                  <tr key={chiaveDi(riga)}>
                    <td>
                      <strong>{riga.descrizione}</strong>
                      {riga.codice_articolo && (
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                          codice {riga.codice_articolo}
                          {riga.prezzo_medio != null
                            && ` — ${euro(riga.prezzo_medio)}/${riga.unita_misura || 'unità'}`}
                        </div>
                      )}
                    </td>
                    <td>{riga.fornitore_nome || <em>da attribuire</em>}</td>
                    <td className="numero">{riga.volte}</td>
                    <td className="numero">{euro(riga.importo_totale)}</td>
                    <td>
                      <Select
                        value={scelte[chiaveDi(riga)] ?? ''}
                        onChange={(e) => setScelte({
                          ...scelte,
                          [chiaveDi(riga)]: e.target.value === 'nessuno'
                            ? 'nessuno'
                            : (e.target.value ? Number(e.target.value) : '')
                        })}
                      >
                        <option value="">Scegli…</option>
                        <option value="nessuno">Non e' un ingrediente</option>
                        {ingredienti.map((i) => (
                          <option key={i.id} value={i.id}>{i.nome}</option>
                        ))}
                      </Select>
                    </td>
                    <td>
                      <Button
                        $size="sm"
                        disabled={!scelte[chiaveDi(riga)]}
                        onClick={() => abbina(riga)}
                      >
                        Conferma
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabella>
          </CardBody></Card>
        )}
      </Sezione>

      <Sezione>
        <Titolo><TrendingUp size={18} /> Quanto paghiamo davvero</Titolo>
        <Nota>
          Il prezzo medio pagato nell'ultimo anno, dalle fatture, contro il costo scritto
          in anagrafica — quello con cui si calcola il food cost di ogni gusto. Dove i due
          numeri si allontanano, il food cost e' sbagliato in silenzio.
        </Nota>

        {prezzi.length === 0 ? (
          <Alert $variant="info">
            Ancora nessuna riga abbinata: il confronto compare quando le voci sono
            associate agli ingredienti.
          </Alert>
        ) : (
          <Card><CardBody>
            <Tabella>
              <thead>
                <tr>
                  <th>Ingrediente</th>
                  <th>Fornitore</th>
                  <th className="numero">Pagato</th>
                  <th className="numero">In anagrafica</th>
                  <th className="numero">Differenza</th>
                  <th className="numero">Speso</th>
                </tr>
              </thead>
              <tbody>
                {prezzi.map((riga) => {
                  const pagato = Number(riga.prezzo_medio || 0);
                  const scritto = Number(riga.costo_in_anagrafica || 0);
                  const scarto = scritto > 0 ? (pagato - scritto) / scritto : 0;
                  return (
                    <tr key={`${riga.ingrediente_id}-${riga.fornitore_id}`}>
                      <td>{riga.ingrediente}</td>
                      <td>{riga.fornitore_nome || '—'}</td>
                      <td className="numero">{euro(pagato)}/{riga.unita_misura}</td>
                      <td className="numero">{euro(scritto)}/{riga.unita_misura}</td>
                      <td className="numero">
                        <Scarto $grave={Math.abs(scarto) > 0.1}>
                          {scritto > 0
                            ? `${scarto > 0 ? '+' : ''}${(scarto * 100).toFixed(0)}%`
                            : '—'}
                        </Scarto>
                      </td>
                      <td className="numero">{euro(riga.speso)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabella>
          </CardBody></Card>
        )}
      </Sezione>

      <Sezione>
        <Titolo><FileText size={18} /> Fatture importate</Titolo>
        <Card><CardBody>
          <Tabella>
            <thead>
              <tr>
                <th>Data</th>
                <th>Numero</th>
                <th>Fornitore</th>
                <th className="numero">Imponibile</th>
                <th className="numero">Totale</th>
                <th className="numero">Righe</th>
              </tr>
            </thead>
            <tbody>
              {fatture.slice(0, 100).map((fattura) => (
                <tr key={fattura.id}>
                  <td>{fattura.data ? new Date(fattura.data).toLocaleDateString('it-IT') : '—'}</td>
                  <td>{fattura.numero}</td>
                  <td>
                    {fattura.fornitore_nome
                      || <em>{fattura.fornitore_denominazione}</em>}
                  </td>
                  <td className="numero">{euro(fattura.imponibile)}</td>
                  <td className="numero">{euro(fattura.totale)}</td>
                  <td className="numero">
                    {fattura.righe}
                    {(fattura.righe_da_abbinare || 0) > 0 && (
                      <Badge $variant="warning" style={{ marginLeft: 6 }}>
                        {fattura.righe_da_abbinare} da abbinare
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabella>
        </CardBody></Card>
      </Sezione>
    </>
  );
};

export default FattureTab;
